import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import {
  CustomerGuest,
  CustomerGuestCreatePayload,
  CustomerGuestListParams,
  CustomerGuestUpdatePayload,
  CustomerGuestsPage,
} from 'src/models/customer-guest.model';

@Injectable({ providedIn: 'root' })
export class CustomerGuestsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api_url}api/v1/customer/guests`;

  listGuests(params: CustomerGuestListParams = {}): Observable<CustomerGuestsPage> {
    return this.http
      .get<CustomerGuest[]>(this.baseUrl, {
        observe: 'response',
        params: this.buildListParams(params),
      })
      .pipe(map((response) => this.toPage(response, params)));
  }

  getGuest(id: number): Observable<CustomerGuest> {
    return this.http.get<CustomerGuest>(`${this.baseUrl}/${id}`);
  }

  createGuest(payload: CustomerGuestCreatePayload): Observable<CustomerGuest> {
    return this.http.post<CustomerGuest>(this.baseUrl, payload);
  }

  updateGuest(id: number, payload: CustomerGuestUpdatePayload): Observable<CustomerGuest> {
    return this.http.put<CustomerGuest>(`${this.baseUrl}/${id}`, payload);
  }

  // Backend returns 404 for an already-deleted guest. Per the frontend handoff,
  // treat that as success so optimistic UI removals don't surface confusing errors.
  deleteGuest(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError((err: HttpErrorResponse) =>
        err.status === 404 ? of(void 0) : throwError(() => err)
      )
    );
  }

  private buildListParams(params: CustomerGuestListParams): HttpParams {
    let httpParams = new HttpParams()
      .set('page', String(params.page ?? 1))
      .set('per_page', String(params.perPage ?? 10));

    const filters = params.filters ?? {};
    (['name', 'email', 'phone'] as const).forEach((key) => {
      const value = filters[key]?.trim();
      if (value) {
        httpParams = httpParams.set(`filters[${key}]`, value);
      }
    });
    return httpParams;
  }

  private toPage(
    response: HttpResponse<CustomerGuest[]>,
    params: CustomerGuestListParams
  ): CustomerGuestsPage {
    const headers = response.headers;
    const guests = response.body ?? [];
    return {
      guests,
      totalCount: this.firstHeaderNumber(headers, ['X-Total-Count', 'X-Total-Records']),
      totalPages: this.firstHeaderNumber(headers, ['X-Total-Pages']),
      currentPage: this.firstHeaderNumber(headers, ['X-Current-Page', 'X-Page']) || params.page || 1,
      perPage: this.firstHeaderNumber(headers, ['X-Per-Page', 'X-Page-Size']) || params.perPage || 10,
    };
  }

  private firstHeaderNumber(headers: HttpResponse<unknown>['headers'], names: string[]): number {
    for (const name of names) {
      const raw = headers.get(name);
      if (raw !== null && raw !== '') {
        const value = Number(raw);
        if (!Number.isNaN(value)) {
          return value;
        }
      }
    }
    return 0;
  }
}
